import { getMunicipalityMapData } from '@api/endpoints'
import html from './template.html'
import effects from './effects'
import initialState from './state'

const template = document.createElement('template')
template.innerHTML = html

customElements.define(
    'municipality-picker',
    class MunicipalityPickerElement extends HTMLElement {
        constructor() {
            super()

            const shadow = this.attachShadow({ mode: 'open' })
            shadow.append(template.content.cloneNode(true))

            /**
             * As we cannot use public class fields, example: onCountyChange = () => {},
             * we need to bind the instance to the methods as these methods will be used
             * as event handlers.
             */
            this.getData = this.getData.bind(this)
            this.onCountyChange = this.onCountyChange.bind(this)
            this.onCountyHighlight = this.onCountyHighlight.bind(this)
            this.onMunicipalityClick = this.onMunicipalityClick.bind(this)
            this.onOffsetChange = this.onOffsetChange.bind(this)
            this.onOffsetReset = this.onOffsetReset.bind(this)
            this.onMunicipalityHighlight =
                this.onMunicipalityHighlight.bind(this)
            this.onMunicipalitySearch = this.onMunicipalitySearch.bind(this)

            /**
             * Create a state which triggers a function and fires an event when the data is changed.
             * With this we can notify other elements that the data has been changed.
             */
            this.state = new Proxy(initialState, {
                set: (state, key, value) => {
                    /**
                     * Use the default behavior.
                     */
                    Reflect.set(state, key, value)

                    /**
                     * Call specific render functions based on a the changed property key.
                     */
                    const effect = effects[key]
                    if (typeof effect === 'function') {
                        effect(state, this)
                    }

                    /**
                     * Debug the state.
                     */
                    console.debug(
                        `%cState update%c${key}`,
                        'background: #51277f; color: #fff; padding: 0 5px',
                        'background: #fdc500; color: #333; padding: 0 5px',
                        value
                    )

                    /**
                     * Notify that the state has been changed.
                     */
                    this.dispatchEvent(
                        new CustomEvent('municipalitystatechange', {
                            bubbles: true,
                            detail: {
                                key,
                                value,
                                state,
                            },
                        })
                    )

                    return true
                },
            })
        }

        /**
         * Gets the filter slot from the Shadow Dom.
         */
        get filterSlot() {
            return this.shadowRoot.querySelector('slot[name="filter"]')
        }

        /**
         * Gets the first assigned filter in to slot element.
         */
        get filter() {
            const elements = this.filterSlot.assignedElements()

            if (!elements.length) {
                return null
            }

            const [filter] = elements
            return filter
        }

        /**
         * Gets the list slot from the Shadow Dom.
         */
        get listSlot() {
            return this.shadowRoot.querySelector('slot[name="list"]')
        }

        /**
         * Gets the first assigned list in to slot element.
         */
        get list() {
            const elements = this.listSlot.assignedElements()

            if (!elements.length) {
                return null
            }

            const [list] = elements
            return list
        }

        /**
         * Gets the map slot from the Shadow Dom.
         */
        get mapSlot() {
            return this.shadowRoot.querySelector('slot[name="map"]')
        }

        /**
         * Gets the first assigned map in to slot element.
         */
        get map() {
            const elements = this.mapSlot.assignedElements()

            if (!elements.length) {
                return null
            }

            const [map] = elements
            return map
        }

        /**
         * Update the state.
         * @param {object} newState
         */
        updateState(newState) {
            for (const key in newState) {
                if (this.state.hasOwnProperty(key)) {
                    this.state[key] = newState[key]
                }
            }
        }

        /**
         * Get the municipality map data and update the state.
         */
        getData() {
            getMunicipalityMapData()
                .then(state => this.updateState(state))
                .catch(error => {
                    console.debug(
                        `%c${error.message}%c${error}`,
                        'background: #51277f; color: #fff; padding: 0 5px',
                        'background: #f00; color: #fff; padding: 0 5px'
                    )
                })
        }

        /**
         * County change event handler.
         * @param {Event} event
         */
        onCountyChange(event) {
            this.updateState({
                selectedCounty: event.detail.statcode,
                offset: 0,
            })
            event.stopPropagation()
        }

        /**
         * County highlight event handler.
         * @param {Event} event
         */
        onCountyHighlight(event) {
            this.updateState({
                highlightedCounty: event.detail.statcode,
            })
            event.stopPropagation()
        }

        /**
         * County highlight event handler.
         * @param {Event} event
         */
        onOffsetChange(event) {
            this.updateState({
                offset: this.state.offset + 1,
            })
            event.stopPropagation()
        }

        /**
         * Reset thhe offset
         * @param {Event} event
         */
        onOffsetReset(event) {
            this.updateState({
                offset: 0,
            })
            event.stopPropagation()
        }

        /**
         * Municipality highlight event handler.
         * @param {Event} event
         */
        onMunicipalityHighlight(event) {
            this.updateState({
                highlightedMunicipality: event.detail.statcode,
            })
            event.stopPropagation()
        }

        /**
         * Municipality click event handler.
         * @param {Event} event
         */
        onMunicipalityClick(event) {
            const municipalities = this.state.data.flatMap(
                ({ municipalities }) => municipalities
            )

            const municipality = municipalities.find(
                municipality => municipality.statcode === event.detail.statcode
            )

            const link = municipality.href
            window.open(link, '_blank')
            event.stopPropagation()
        }

        onMunicipalitySearch(event) {
            this.updateState({
                query: event.detail.query,
            })
        }

        /**
         * Listen to the events and start getting the data.
         */
        connectedCallback() {
            this.addEventListener('municipalitymapready', this.getData)
            this.addEventListener('municipalityclick', this.onMunicipalityClick)
            this.addEventListener('offsetchange', this.onOffsetChange)
            this.addEventListener('offsetreset', this.onOffsetReset)
            this.addEventListener(
                'municipalityhighlight',
                this.onMunicipalityHighlight
            )
            this.addEventListener(
                'municipalitysearch',
                this.onMunicipalitySearch
            )
            this.addEventListener('countychange', this.onCountyChange)
            this.addEventListener('countyhighlight', this.onCountyHighlight)
        }

        /**
         * Remove event listeners.
         */
        disconnectedCallback() {
            this.removeEventListener('municipalitymapready', this.getData)
            this.removeEventListener(
                'municipalityclick',
                this.onMunicipalityClick
            )
            this.removeEventListener('offsetchange', this.onOffsetChange)
            this.removeEventListener('offsetreset', this.onOffsetReset)
            this.removeEventListener(
                'municipalityhighlight',
                this.onMunicipalityHighlight
            )
            this.addEventListener(
                'municipalitysearch',
                this.onMunicipalitySearch
            )
            this.removeEventListener('countychange', this.onCountyChange)
            this.removeEventListener('countyhighlight', this.onCountyHighlight)
        }
    }
)



// WEBPACK FOOTER //
// ./scripts/web-components/municipality-picker/index.js